import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useWatch } from 'react-hook-form-mui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, IconButton, Typography } from '@mui/material';
import {
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
} from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import { useAuth, useBusinessSelector, usePaging } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessMenuItemDetails } from '@barboxx/models';
import { OrderStatus } from '@barboxx/types';
import { getBusinessDayQueryRange } from '@barboxx/utils';

// Generated by Copilot

type InventoryItemRow = {
    id: string;
    parentId: number;
    rowType: 'parent' | 'pour' | 'brand';
    pourKey?: string;
    type: string;
    name: string;
    pourType: string;
    brandName: string;
    itemName: string;
    drinksOrdered: number;
    hasChildren: boolean;
    isExpanded: boolean;
};

const COLUMNS: GridColDef<InventoryItemRow>[] = [
    {
        field: 'parentId',
        headerName: 'ID',
        width: 100,
        valueGetter: (_, row) => row.parentId,
    },
    {
        field: 'type',
        headerName: 'Drink Type',
        width: 200,
    },
    {
        field: 'name',
        headerName: 'Drink Name',
        width: 260,
        sortable: false,
    },
    {
        field: 'pourType',
        headerName: 'Pour Type',
        width: 150,
    },
    {
        field: 'brandName',
        headerName: 'Brand Name',
        width: 200,
        flex: 1,
    },
    {
        field: 'itemName',
        headerName: 'Item Name',
        width: 200,
        flex: 1,
    },
    {
        field: 'drinksOrdered',
        headerName: 'Drinks Ordered',
        width: 200,
    },
];

export interface FilterFormProps {
    startedAt?: Date;
    endedAt?: Date;
    status: OrderStatus[];
}

export function InventoryItemsTable() {
    const {
        auth: { user },
    } = useAuth();
    const { businessId, businessList } = useBusinessSelector();

    const selectedBusiness = React.useMemo(() => {
        if (businessId) {
            return businessList.find(business => business.id === businessId);
        }

        if (user.userType === 'Business' && businessList.length === 1) {
            return businessList[0];
        }

        return undefined;
    }, [businessId, businessList, user.userType]);

    const { status, startedAt, endedAt } = useWatch();
    const { startDateTime, endDateTime } = getBusinessDayQueryRange(
        startedAt,
        endedAt,
        {
            hoursOfOperations: selectedBusiness?.place?.hoursOfOperations,
        }
    );

    const { page, size, sort, updatePageModel, updateSort } = usePaging();
    const [expandedParentRows, setExpandedParentRows] = React.useState<
        Record<number, boolean>
    >({});
    const [expandedPourRows, setExpandedPourRows] = React.useState<
        Record<string, boolean>
    >({});
    const { data, isLoading, error } = useQuery({
        queryKey: [
            'inventoryItems',
            page,
            size,
            sort,
            status,
            businessId,
            startDateTime,
            endDateTime,
        ],
        queryFn: () =>
            Api.Menu.Business.getInventoryData({
                page,
                size,
                sort,
                status,
                businessId,
                startDateTime,
                endDateTime,
            }),
    });

    const toggleParentRow = React.useCallback((parentId: number) => {
        setExpandedParentRows(prev => ({
            ...prev,
            [parentId]: !prev[parentId],
        }));
    }, []);

    const togglePourRow = React.useCallback((pourKey: string) => {
        setExpandedPourRows(prev => ({
            ...prev,
            [pourKey]: !prev[pourKey],
        }));
    }, []);

    React.useEffect(() => {
        const groupedRows =
            (data?.data?.results as BusinessMenuItemDetails.InventoryItem[]) ??
            [];

        const validParentIds = new Set<number>();
        const validPourKeys = new Set<string>();

        for (const parent of groupedRows) {
            const variantsByPourType = new Map<
                string,
                BusinessMenuItemDetails.InventoryVariant[]
            >();

            for (const variant of parent.variants) {
                const pourType = variant.pourType || 'N/A';
                const variants = variantsByPourType.get(pourType) ?? [];
                variants.push(variant);
                variantsByPourType.set(pourType, variants);
            }

            if (variantsByPourType.size > 1) {
                validParentIds.add(parent.id);
            }

            for (const [pourType, variants] of variantsByPourType.entries()) {
                const uniqueBrands = new Set(
                    variants.map(variant => variant.brandName || 'N/A')
                );

                if (uniqueBrands.size > 1 && variantsByPourType.size > 1) {
                    validPourKeys.add(`${parent.id}:${pourType}`);
                }
            }
        }

        setExpandedParentRows(prev => {
            const next: Record<number, boolean> = {};

            for (const [key, value] of Object.entries(prev)) {
                const parentId = Number(key);

                if (validParentIds.has(parentId) && value) {
                    next[parentId] = true;
                }
            }

            return next;
        });

        setExpandedPourRows(prev => {
            const next: Record<string, boolean> = {};

            for (const [key, value] of Object.entries(prev)) {
                if (validPourKeys.has(key) && value) {
                    next[key] = true;
                }
            }

            return next;
        });
    }, [data?.data?.results]);

    const handleChangePageModel = (pageModel: GridPaginationModel) => {
        updatePageModel(pageModel);
    };

    const rows = React.useMemo(() => {
        const groupedRows =
            (data?.data?.results as BusinessMenuItemDetails.InventoryItem[]) ??
            [];

        return groupedRows.flatMap(parent => {
            const variantsByPourType = new Map<
                string,
                BusinessMenuItemDetails.InventoryVariant[]
            >();

            for (const variant of parent.variants) {
                const pourType = variant.pourType || 'N/A';
                const variants = variantsByPourType.get(pourType) ?? [];
                variants.push(variant);
                variantsByPourType.set(pourType, variants);
            }

            const hasMultiplePourTypes = variantsByPourType.size > 1;
            const isParentExpanded = !!expandedParentRows[parent.id];

            if (!hasMultiplePourTypes) {
                const variants = Array.from(variantsByPourType.values()).flat();
                const singlePourType = variants[0]?.pourType ?? 'N/A';
                const uniqueBrandNames = Array.from(
                    new Set(
                        variants
                            .map(variant => variant.brandName)
                            .filter(
                                brandName => !!brandName && brandName !== 'N/A'
                            )
                    )
                ).sort((left, right) => left.localeCompare(right));

                return [
                    {
                        id: `parent-${parent.id}`,
                        parentId: parent.id,
                        rowType: 'parent' as const,
                        type: parent.type,
                        name: parent.name,
                        pourType: singlePourType,
                        brandName:
                            uniqueBrandNames.length > 0
                                ? uniqueBrandNames.join(' / ')
                                : 'N/A',
                        itemName: parent.itemName,
                        drinksOrdered: parent.drinksOrdered,
                        hasChildren: false,
                        isExpanded: false,
                    },
                ];
            }

            const parentRow: InventoryItemRow = {
                id: `parent-${parent.id}`,
                parentId: parent.id,
                rowType: 'parent',
                type: parent.type,
                name: parent.name,
                pourType: '-',
                brandName: '-',
                itemName: parent.itemName,
                drinksOrdered: parent.drinksOrdered,
                hasChildren: true,
                isExpanded: isParentExpanded,
            };

            if (!isParentExpanded) {
                return [parentRow];
            }

            const pourRows: InventoryItemRow[] = [];

            for (const [pourType, variants] of Array.from(
                variantsByPourType.entries()
            ).sort((left, right) => left[0].localeCompare(right[0]))) {
                const pourKey = `${parent.id}:${pourType}`;
                const variantsByBrand = new Map<
                    string,
                    {
                        brandName: string;
                        itemName: string;
                        drinksOrdered: number;
                    }
                >();

                for (const variant of variants) {
                    const normalizedBrandName = variant.brandName || 'N/A';
                    const existingBrand =
                        variantsByBrand.get(normalizedBrandName);

                    if (existingBrand) {
                        existingBrand.drinksOrdered += variant.drinksOrdered;
                        continue;
                    }

                    variantsByBrand.set(normalizedBrandName, {
                        brandName: normalizedBrandName,
                        itemName: variant.itemName,
                        drinksOrdered: variant.drinksOrdered,
                    });
                }

                const sortedBrands = Array.from(variantsByBrand.values()).sort(
                    (left, right) =>
                        left.brandName.localeCompare(right.brandName)
                );

                const hasMultipleBrands = sortedBrands.length > 1;
                const isPourExpanded = !!expandedPourRows[pourKey];
                const pourTotal = variants.reduce(
                    (sum, variant) => sum + variant.drinksOrdered,
                    0
                );

                const pourRow: InventoryItemRow = {
                    id: `pour-${pourKey}`,
                    parentId: parent.id,
                    rowType: 'pour',
                    pourKey,
                    type: parent.type,
                    name: parent.name,
                    pourType,
                    brandName: hasMultipleBrands
                        ? '-'
                        : (sortedBrands[0]?.brandName ?? 'N/A'),
                    itemName: sortedBrands[0]?.itemName ?? parent.itemName,
                    drinksOrdered: pourTotal,
                    hasChildren: hasMultipleBrands,
                    isExpanded: hasMultipleBrands ? isPourExpanded : false,
                };

                pourRows.push(pourRow);

                if (hasMultipleBrands && isPourExpanded) {
                    const brandRows: InventoryItemRow[] = sortedBrands.map(
                        brand => ({
                            id: `brand-${pourKey}:${brand.brandName}`,
                            parentId: parent.id,
                            rowType: 'brand',
                            pourKey,
                            type: parent.type,
                            name: parent.name,
                            pourType,
                            brandName: brand.brandName,
                            itemName: brand.itemName,
                            drinksOrdered: brand.drinksOrdered,
                            hasChildren: false,
                            isExpanded: false,
                        })
                    );

                    pourRows.push(...brandRows);
                }
            }

            return [parentRow, ...pourRows];
        });
    }, [data?.data?.results, expandedParentRows, expandedPourRows]);

    const FINAL_COLUMNS = React.useMemo(
        () =>
            COLUMNS.map(column => {
                if (column.field !== 'name') {
                    return column;
                }

                return {
                    ...column,
                    renderCell: (
                        params: GridRenderCellParams<InventoryItemRow>
                    ) => {
                        const row = params.row;

                        if (row.rowType === 'brand') {
                            return (
                                <Box sx={{ pl: 9, width: '100%' }}>
                                    <Typography variant="body2">
                                        ↳ {row.brandName}
                                    </Typography>
                                </Box>
                            );
                        }

                        if (row.rowType === 'pour') {
                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        pl: 4,
                                    }}
                                >
                                    {row.hasChildren ? (
                                        <IconButton
                                            size="small"
                                            onClick={event => {
                                                event.stopPropagation();

                                                if (row.pourKey) {
                                                    togglePourRow(row.pourKey);
                                                }
                                            }}
                                            sx={{ mr: 1 }}
                                        >
                                            {row.isExpanded ? (
                                                <KeyboardArrowDownIcon fontSize="small" />
                                            ) : (
                                                <KeyboardArrowRightIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    ) : (
                                        <Box sx={{ width: 32, mr: 1 }} />
                                    )}
                                    <Typography variant="body2">
                                        ↳ {row.pourType}
                                    </Typography>
                                </Box>
                            );
                        }

                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                {row.hasChildren ? (
                                    <IconButton
                                        size="small"
                                        onClick={event => {
                                            event.stopPropagation();
                                            toggleParentRow(row.parentId);
                                        }}
                                        sx={{ mr: 1 }}
                                    >
                                        {row.isExpanded ? (
                                            <KeyboardArrowDownIcon fontSize="small" />
                                        ) : (
                                            <KeyboardArrowRightIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                ) : (
                                    <Box sx={{ width: 32, mr: 1 }} />
                                )}
                                <Typography variant="body2">
                                    {row.name}
                                </Typography>
                            </Box>
                        );
                    },
                };
            }),
        [toggleParentRow, togglePourRow]
    );

    return (
        <DataGridCard>
            <DataGrid
                columns={FINAL_COLUMNS}
                disableColumnMenu
                disableRowSelectionOnClick
                error={error}
                loading={isLoading}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                onSortModelChange={updateSort}
                rows={rows}
                rowCount={data?.data?.total ?? 0}
            />
        </DataGridCard>
    );
}
